/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {Area} from "react-easy-crop";

interface GetCroppedImageProps {
	originalImage: string;
	crop: Area;
}

const getCroppedImg = async ({originalImage, crop}: GetCroppedImageProps): Promise<File> => {
	const image = new Image();
	image.src = originalImage;

	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");

	function calculateQuality(_width: number, _height: number) {
		const sum = _width + _height;
		const maxSum = 9000;
		const minSum = 4000;
		const maxQuality = 1;
		const minQuality = 0.5;

		// Linear interpolation formula
		const quality = maxQuality - ((sum - minSum) / (maxSum - minSum)) * (maxQuality - minQuality);

		// Clamp the quality value between minQuality and maxQuality
		return Math.max(minQuality, Math.min(maxQuality, quality));
	}

	if (!ctx) {
		alert("This browser don't support canvas 2d");
		throw new Error("This browser don't support canvas 2d");
	}

	if (image.width === 0) {
		throw new Error("Image width can't be zero");
	}

	if (image.height === 0) {
		throw new Error("Image height can't be zero");
	}

	const scaleX = image.naturalWidth / image.width;
	const scaleY = image.naturalHeight / image.height;
	const x = crop.x * scaleX;
	const y = crop.y * scaleY;
	const width = crop.width * scaleX;
	const height = crop.height * scaleY;

	canvas.width = width;
	canvas.height = height;

	const qualityParam = calculateQuality(width, height);

	ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (blob) {
					const croppedFile = new File([blob], "croppedImage.jpeg");
					resolve(croppedFile);
				} else {
					reject(new Error("Error creating blob"));
				}
			},
			"image/jpeg",
			qualityParam
		);
	});

};

export default getCroppedImg;
